import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Card,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  Wifi,
  Phone,
  Tv,
  Zap as ElectricityIcon,
  GraduationCap,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  getServices,
  getServiceProviders,
  updateProduct,
  buildV2RoutesFromLegacy,
} from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';
import MultiProcessorRoutingCard from './components/Services/MultiProcessorRoutingCard';

// Service definitions
const SERVICES_LIST = [
  {
    key: 'Data',
    title: 'Data bundles',
    icon: Wifi,
    color: '#0066FE',
    bg: '#EFF6FF',
    defaultValidity: '30 Days',
    presetCategories: [
      'MTN SME',
      'MTN CG',
      'MTN Direct Coupon',
      'AIRTEL CG',
      'AIRTEL SME',
      'GLO SME',
      'GLO Direct',
      '9MOBILE SME',
    ],
  },
  {
    key: 'Airtime',
    title: 'Airtime VTU',
    icon: Phone,
    color: '#16A34A',
    bg: '#DCFCE7',
    defaultValidity: 'Instant VTU',
    presetCategories: ['MTN', 'AIRTEL', 'GLO', '9MOBILE'],
  },
  {
    key: 'Cable',
    title: 'Cable TV',
    icon: Tv,
    color: '#D97706',
    bg: '#FEF3C7',
    defaultValidity: 'Monthly',
    presetCategories: ['DSTV', 'GOTV', 'STARTIMES', 'SHOWMAX'],
  },
  {
    key: 'Electricity',
    title: 'Electricity bills',
    icon: ElectricityIcon,
    color: '#EA580C',
    bg: '#FFEDD5',
    defaultValidity: 'Instant Token',
    presetCategories: [
      'Jos Electricity',
      'Ikeja Electricity',
      'Eko Electricity',
      'Abuja Electricity',
      'Ibadan Electricity',
      'Enugu Electricity',
      'Kano Electricity',
      'Port Harcourt Electricity',
      'Benin Electricity',
      'Kaduna Electricity',
      'Yola Electricity',
    ],
  },
  {
    key: 'ResultCheck',
    title: 'Result checkers',
    icon: GraduationCap,
    color: '#7C3AED',
    bg: '#EDE9FE',
    defaultValidity: 'Instant e-PIN',
    presetCategories: ['WAEC', 'NECO', 'NABTEB'],
  },
];

// Robust brand logo finder
const getBrandLogo = (category, details) => {
  const text = `${category || ''} ${details?.name || ''}`
    .toLowerCase()
    .replace(/[\s\-_]/g, '');
  const matchedKey = Object.keys(BRAND_IMAGES).find((key) => text.includes(key));
  return matchedKey ? BRAND_IMAGES[matchedKey] : null;
};

export default function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [saving, setSaving] = useState(false);

  // Active Service State
  const [selectedService, setSelectedService] = useState('Data');
  const [isScopedService, setIsScopedService] = useState(true);

  // Selected Network for telecom services (MTN, Airtel, Glo, 9mobile)
  const [selectedDataNetwork, setSelectedDataNetwork] = useState('MTN');

  const activeServiceConfig = useMemo(
    () => SERVICES_LIST.find((s) => s.key === selectedService) || SERVICES_LIST[0],
    [selectedService]
  );

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    amount: '',
    vipAmount: '',
    buyingAmount: '',
    discount: 0,
    fee: 0,
    cashback: 0,
    referralBonus: 0,
    validity: '',
    descriptions: '',
    planId: '',
    planNetworkId: '1',
    available: true,
    hidden: false,
    allowApi: true,
    allowUssd: true,
    allowWhatsapp: true,
  });

  // V2 Router State
  const [useV2Engine, setUseV2Engine] = useState(false);
  const [processorRoutes, setProcessorRoutes] = useState([]);

  // Connected Vendors & Provider Catalog
  const [providers, setProviders] = useState([]);

  // Fetch initial product & providers data
  const fetchData = useCallback(async () => {
    setLoadingInitial(true);
    try {
      const [srvRes, provRes] = await Promise.all([
        getServices(),
        getServiceProviders(),
      ]);

      if (provRes.ok && Array.isArray(provRes.data?.data)) {
        setProviders(provRes.data.data);
      }

      if (srvRes.ok && srvRes.data?.data) {
        const found = srvRes.data.data.find((s) => String(s.id) === String(id));
        if (found) {
          setProduct(found);

          const srvType = found.service || 'Data';
          setSelectedService(srvType);

          // Detect network from product
          let net = (found.network || '').toUpperCase();
          if (!net) {
            const combined = `${found.category || ''} ${found.details?.name || ''}`.toUpperCase();
            if (combined.includes('MTN')) net = 'MTN';
            else if (combined.includes('AIRTEL')) net = 'AIRTEL';
            else if (combined.includes('GLO')) net = 'GLO';
            else if (combined.includes('9MOBILE') || combined.includes('ETISALAT')) net = '9MOBILE';
            else net = 'MTN';
          }
          setSelectedDataNetwork(net);

          const netIdMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
          let derivedNetId = netIdMap[net] || '1';
          if (found.networkId) {
            derivedNetId = String(found.networkId);
          } else if (found.details?.networkId) {
            derivedNetId = String(found.details.networkId);
          }

          setFormData({
            name: found.name || found.details?.name || '',
            category: found.category || '',
            amount: found.details?.amount || found.amount || '',
            vipAmount: found.vipAmount || '',
            buyingAmount: found.buyingAmount || '',
            discount: found.discount || 0,
            fee: found.fee || 0,
            cashback: found.cashback || 0,
            referralBonus: found.referralBonus || 0,
            validity: found.validity || '',
            descriptions: found.descriptions || '',
            planId:
              found.planId ||
              found.details?.planId ||
              found.details?.plan_id ||
              found.processor?.payload?.plan ||
              found.processor?.payload?.cableplan ||
              found.processor?.payload?.exam_name ||
              '',
            planNetworkId: derivedNetId,
            available: found.available ?? true,
            hidden: found.hidden ?? false,
            allowApi: found.allowApi ?? true,
            allowUssd: found.allowUssd ?? true,
            allowWhatsapp: found.allowWhatsapp ?? true,
          });

          setUseV2Engine(Boolean(found.useV2Engine));
          const existingRoutes = found.processorRoutes || [];
          if (existingRoutes.length > 0) {
            setProcessorRoutes(existingRoutes);
          } else {
            setProcessorRoutes(buildV2RoutesFromLegacy(found, { networkId: derivedNetId }));
          }
        } else {
          toast.error(`Product #${id} was not found.`);
        }
      }
    } catch (err) {
      toast.error('Error fetching product details.');
    } finally {
      setLoadingInitial(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle switching Data / Airtime Network (MTN, Airtel, Glo, 9mobile)
  const handleDataNetworkChange = (networkKey, networkId) => {
    setSelectedDataNetwork(networkKey);
    setFormData((prev) => {
      let matchingPreset = activeServiceConfig.presetCategories.find((c) =>
        c.toUpperCase().startsWith(networkKey.toUpperCase())
      );
      if (!matchingPreset) {
        matchingPreset = selectedService === 'Airtime' ? networkKey : `${networkKey} SME`;
      }

      let newName = prev.name;
      if (selectedService === 'Airtime') {
        newName = `${networkKey} Airtime VTU`;
      }

      return {
        ...prev,
        category: matchingPreset,
        name: newName,
        planNetworkId: networkId ? String(networkId) : prev.planNetworkId,
      };
    });
  };

  // Scoped preset categories for Section 2
  const visiblePresets = useMemo(() => {
    if ((selectedService === 'Data' || selectedService === 'Airtime') && selectedDataNetwork) {
      const filtered = activeServiceConfig.presetCategories.filter((preset) =>
        preset.toUpperCase().startsWith(selectedDataNetwork.toUpperCase())
      );
      if (filtered.length > 0) return filtered;
    }
    return activeServiceConfig.presetCategories;
  }, [selectedService, selectedDataNetwork, activeServiceConfig]);

  // When changing service tab, adjust defaults
  const handleServiceChange = (serviceKey) => {
    setSelectedService(serviceKey);
    const cfg = SERVICES_LIST.find((s) => s.key === serviceKey);
    if (cfg) {
      let defaultCategory = cfg.presetCategories[0] || '';
      let defaultNetId = '1';
      let defaultName = '';

      if (serviceKey === 'Data') {
        const netPreset =
          cfg.presetCategories.find((c) =>
            c.toUpperCase().startsWith(selectedDataNetwork.toUpperCase())
          ) || cfg.presetCategories[0];
        defaultCategory = netPreset;
        const netIdMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
        defaultNetId = netIdMap[selectedDataNetwork] || '1';
      } else if (serviceKey === 'Airtime') {
        defaultCategory = selectedDataNetwork || 'MTN';
        defaultName = `${defaultCategory} Airtime VTU`;
        const netIdMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
        defaultNetId = netIdMap[selectedDataNetwork] || '1';
      }

      setFormData((prev) => ({
        ...prev,
        category: defaultCategory,
        validity: cfg.defaultValidity,
        name: defaultName,
        planNetworkId: defaultNetId,
      }));
    }
  };

  // Sync plan info callback from MultiProcessorRoutingCard
  const handleSyncPlan = ({ plan, isManualSync }) => {
    if (!plan) return;
    setFormData((prev) => ({
      ...prev,
      planId: String(plan.planId),
      buyingAmount: (!prev.buyingAmount || isManualSync) ? String(plan.costPrice || 0) : prev.buyingAmount,
      name: (!prev.name || isManualSync) ? (plan.planName || prev.name) : prev.name,
      validity: (!prev.validity || isManualSync) ? (plan.validity || prev.validity) : prev.validity,
    }));
  };

  // Profit Margin Live Calculations
  const isAirtime = selectedService === 'Airtime';
  const isElectricity = selectedService === 'Electricity';

  const discountNum = Number(formData.discount || 0);
  const buyingNum = Number(formData.buyingAmount || 0);
  const vipAirtimeDiscount = Number(formData.vipAmount || 0);
  const airtimeMargin = buyingNum - discountNum;
  const airtimeVipMargin = buyingNum - vipAirtimeDiscount;

  const retailAmount = Number(formData.amount || 0);
  const wholesaleCost = Number(formData.buyingAmount || 0);
  const vipPrice = Number(formData.vipAmount || 0);
  const cashbackAmount = Number(formData.cashback || 0);
  const referralAmount = Number(formData.referralBonus || 0);

  // Accurate Cash Economics
  const grossMargin = retailAmount - wholesaleCost;
  const totalRewards = cashbackAmount + referralAmount;
  const totalExpenses = wholesaleCost + totalRewards;
  const netMargin = retailAmount - totalExpenses;
  const netMarginPercent = retailAmount > 0 ? (netMargin / retailAmount) * 100 : 0;

  // VIP Pricing & Margin (if VIP price is entered)
  const hasVipPrice = vipPrice > 0;
  const vipNetMargin = hasVipPrice ? vipPrice - totalExpenses : null;
  const vipNetMarginPercent = hasVipPrice ? (vipNetMargin / vipPrice) * 100 : 0;

  // Submit Handler: Save Edits
  const handleSaveProduct = async () => {
    if (!product) return;

    if (!formData.category.trim()) {
      toast.error('Please enter or select a category / network.');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a product or plan name.');
      return;
    }

    if (!isAirtime && !formData.amount && !isElectricity) {
      toast.error('Please enter a retail selling price.');
      return;
    }

    setSaving(true);
    try {
      const brandLogo = getBrandLogo(formData.category, { name: formData.name });

      let updatePayload = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        service: selectedService,
        available: formData.available,
        hidden: formData.hidden,
        validity: formData.validity,
        descriptions: formData.descriptions,
        imageUrl: brandLogo || product.imageUrl || '',
        allowApi: formData.allowApi ?? true,
        allowUssd: formData.allowUssd ?? true,
        allowWhatsapp: formData.allowWhatsapp ?? true,
        useV2Engine,
        processorRoutes,
        network: ['Data', 'Airtime'].includes(selectedService) ? selectedDataNetwork : null,
        networkId: Number(formData.planNetworkId || 1),
        planId: formData.planId || '',
      };

      if (isAirtime) {
        updatePayload = {
          ...updatePayload,
          category: formData.category.toUpperCase().trim(),
          amount: 0,
          discount: Number(formData.discount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          fee: Number(formData.fee || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            ...product.details,
            name: formData.name.trim() || `${formData.category} Airtime VTU`,
            amount: 0,
            network: selectedDataNetwork,
            networkId: Number(formData.planNetworkId || 1),
          },
        };
      } else if (selectedService === 'Data') {
        updatePayload = {
          ...updatePayload,
          category: formData.category.toUpperCase().trim(),
          amount: Number(formData.amount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            ...product.details,
            name: formData.name.trim(),
            planName: formData.planId || formData.name.trim(),
            amount: Number(formData.amount || 0),
            custom: true,
          },
          customData: {
            networkid: formData.planNetworkId || '1',
            planid: formData.planId || '1001',
          },
        };
      } else {
        updatePayload = {
          ...updatePayload,
          amount: Number(formData.amount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          fee: Number(formData.fee || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            ...product.details,
            name: formData.name.trim(),
            planName: formData.planId || formData.name.trim(),
            amount:
              Number(formData.amount || 0) > 0
                ? String(formData.amount)
                : product.details?.amount || '0',
          },
        };
      }

      const res = await updateProduct(product.id, updatePayload);
      if (res.ok) {
        toast.success(`Successfully saved changes for ${formData.name || formData.category}!`);
        navigate(`/dashboard/services/details/${product.id}`);
      } else {
        toast.error(res.data?.message || 'Failed to update product.');
      }
    } catch (err) {
      toast.error('An unexpected error occurred while saving product edits.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingInitial) {
    return (
      <Page title="Edit Product | Dashboard">
        <Container maxWidth="md" sx={{ py: 12 }}>
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  if (!product) {
    return (
      <Page title="Product Not Found | Dashboard">
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Button
            startIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/dashboard/services')}
            sx={{ mb: 3 }}
          >
            Back to services
          </Button>
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: '24px' }}>
            <Typography variant="h6" color="text.secondary">
              Product ID #{id} was not found in catalog.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`Edit Product #${product.id} - ${formData.name || product.category} | Dashboard`}>
      <Container maxWidth="md" sx={{ py: 3, maxWidth: 880, mx: 'auto' }}>
        {/* Top Navigation Bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3.5}>
          <Box
            onClick={() => navigate(`/dashboard/services/details/${product.id}`)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#0066FE',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            <span>Back to Product Details</span>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={() => navigate(`/dashboard/services/details/${product.id}`)}
              sx={{
                borderRadius: '24px',
                bgcolor: '#F1F3F5',
                color: '#374151',
                px: 2.75,
                py: 0.9,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProduct}
              disabled={saving}
              sx={{
                borderRadius: '24px',
                bgcolor: '#0066FE',
                color: '#FFFFFF',
                px: 3.25,
                py: 0.9,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0052CC', boxShadow: 'none' },
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save changes'}
            </Button>
          </Stack>
        </Stack>

        {/* Page Header */}
        <Box mb={4}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.75}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, color: '#111827', fontSize: { xs: '1.75rem', sm: '2.25rem' } }}
            >
              Edit Product #{product.id}
            </Typography>
            <Chip
              label={selectedService}
              size="small"
              sx={{
                bgcolor: activeServiceConfig.bg,
                color: activeServiceConfig.color,
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          </Stack>
          <Typography sx={{ color: '#6B7280', fontSize: '0.95rem' }}>
            Modify product settings, retail margins, distribution flags, and configure multi-processor automated failovers.
          </Typography>
        </Box>

        {/* 1. Service Type Selector */}
        <Card
          sx={{
            p: 3,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              1. Service Category
            </Typography>
            {isScopedService && (
              <Button
                size="small"
                onClick={() => setIsScopedService(false)}
                sx={{
                  fontSize: '0.75rem',
                  color: '#0066FE',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#EFF6FF',
                  borderRadius: '16px',
                  px: 1.5,
                  py: 0.35,
                  '&:hover': { bgcolor: '#DBEAFE' },
                }}
              >
                Change Service Category
              </Button>
            )}
          </Stack>

          {isScopedService ? (
            /* Scoped single service view */
            <Box
              sx={{
                p: 2,
                borderRadius: '18px',
                border: `2px solid ${activeServiceConfig.color}`,
                bgcolor: activeServiceConfig.bg,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                minWidth: { xs: '100%', sm: 280 },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: '#FFFFFF',
                  color: activeServiceConfig.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                }}
              >
                {React.createElement(activeServiceConfig.icon, { size: 22 })}
              </Box>
              <div>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: activeServiceConfig.color }}>
                  {activeServiceConfig.title}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: 500 }}>
                  Active Service Category
                </Typography>
              </div>
            </Box>
          ) : (
            /* Full 5-service selector grid when unconstrained */
            <Grid container spacing={1.5}>
              {SERVICES_LIST.map((srv) => {
                const IconComp = srv.icon;
                const isSelected = selectedService === srv.key;
                return (
                  <Grid item xs={6} sm={4} md={2.4} key={srv.key}>
                    <Box
                      onClick={() => handleServiceChange(srv.key)}
                      sx={{
                        p: 2,
                        borderRadius: '18px',
                        border: isSelected ? `2px solid ${srv.color}` : '1px solid #E5E7EB',
                        bgcolor: isSelected ? srv.bg : '#FFFFFF',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                          borderColor: srv.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '12px',
                          bgcolor: isSelected ? '#FFFFFF' : srv.bg,
                          color: srv.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 1,
                        }}
                      >
                        <IconComp size={20} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          color: isSelected ? srv.color : '#111827',
                        }}
                      >
                        {srv.title}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* If Data or Airtime Service is Selected: include Telecom Network Selection */}
          {(selectedService === 'Data' || selectedService === 'Airtime') && (
            <Box mt={3} pt={2.5} sx={{ borderTop: '1px dashed #E5E7EB' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1.5,
                }}
              >
                Telecom Network:
              </Typography>
              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                {[
                  { key: 'MTN', name: 'MTN', color: '#EAB308', networkId: '1' },
                  { key: 'AIRTEL', name: 'Airtel', color: '#EF4444', networkId: '4' },
                  { key: 'GLO', name: 'Glo', color: '#22C55E', networkId: '2' },
                  { key: '9MOBILE', name: '9mobile', color: '#15803D', networkId: '3' },
                ].map((net) => {
                  const isNetSelected = selectedDataNetwork === net.key;
                  return (
                    <Button
                      key={net.key}
                      onClick={() => handleDataNetworkChange(net.key, net.networkId)}
                      startIcon={
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: net.color,
                          }}
                        />
                      }
                      sx={{
                        borderRadius: '20px',
                        px: 2.25,
                        py: 0.65,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: isNetSelected ? '#111827' : '#F3F4F6',
                        color: isNetSelected ? '#FFFFFF' : '#374151',
                        border: isNetSelected ? 'none' : '1px solid #E5E7EB',
                        '&:hover': {
                          bgcolor: isNetSelected ? '#1F2937' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {net.name}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Card>


        {/* 2. Product Name & Network / Category */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            2. Product Information
          </Typography>

          <Stack spacing={3}>
            {/* Quick Category / Network Pills */}
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                {selectedService === 'Data'
                  ? `Suggested ${selectedDataNetwork} Plan Categories`
                  : `Suggested ${selectedService === 'Airtime' ? 'Network' : 'Category / Provider'}`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {visiblePresets.map((preset) => {
                  const isSelected = formData.category.toUpperCase() === preset.toUpperCase();
                  return (
                    <Button
                      key={preset}
                      onClick={() => {
                        const netMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
                        if (selectedService === 'Airtime') {
                          setSelectedDataNetwork(preset.toUpperCase());
                        }
                        setFormData((prev) => {
                          let suggestedName = prev.name;
                          let netId = prev.planNetworkId;
                          if (selectedService === 'Airtime') {
                            suggestedName = `${preset} Airtime VTU`;
                            netId = netMap[preset.toUpperCase()] || '1';
                          } else if (selectedService === 'Electricity') {
                            suggestedName = preset;
                          }
                          return {
                            ...prev,
                            category: preset,
                            name: suggestedName,
                            planNetworkId: netId,
                          };
                        });
                      }}
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.825rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: isSelected ? '#111827' : '#F3F4F6',
                        color: isSelected ? '#FFFFFF' : '#374151',
                        border: isSelected ? 'none' : '1px solid #E5E7EB',
                        '&:hover': {
                          bgcolor: isSelected ? '#1F2937' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {preset}
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category / Network Name"
                  placeholder="e.g. MTN SME, AIRTEL CG, Jos Electricity"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  helperText="Primary classification group"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Plan Name"
                  placeholder="e.g. 20GB Monthly, 1.0GB SME, Jos Electricity"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  helperText="Display name shown on user dashboard and API"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Validity Duration"
                  placeholder="e.g. 30 Days, Instant, Monthly"
                  value={formData.validity}
                  onChange={(e) => setFormData({ ...formData, validity: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description (Optional)"
              placeholder="e.g. Valid for 30 days. Usable on all internet-enabled devices."
              value={formData.descriptions}
              onChange={(e) => setFormData({ ...formData, descriptions: e.target.value })}
            />
          </Stack>
        </Card>

        {/* 3. Pricing & Margins Card */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            3. Pricing & Profit Margin
          </Typography>

          {isAirtime ? (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ py: 0.5, borderRadius: '14px' }}>
                For <strong>Airtime VTU & Share</strong>, enter rate percentages (e.g. 2.0% customer discount, 3.5% wholesale commission).
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retail User Discount (%)"
                    placeholder="2.00"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Wholesale Commission (%)"
                    placeholder="3.50"
                    value={formData.buyingAmount}
                    onChange={(e) => setFormData({ ...formData, buyingAmount: e.target.value })}
                    helperText="Discount granted by upstream provider"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="VIP Member Discount (%)"
                    placeholder="2.50"
                    value={formData.vipAmount}
                    onChange={(e) => setFormData({ ...formData, vipAmount: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Live Airtime Margin Box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  bgcolor: airtimeMargin < 0 ? 'rgba(220, 38, 38, 0.035)' : 'rgba(22, 163, 74, 0.035)',
                  border: '1px solid',
                  borderColor: airtimeMargin < 0 ? 'rgba(220, 38, 38, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                  <div>
                    <Typography sx={{ color: '#4B5563', fontSize: '0.85rem', fontWeight: 650 }}>
                      Estimated Net Airtime Margin
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        color: airtimeMargin >= 0 ? '#16A34A' : '#DC2626',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {airtimeMargin >= 0 ? `+${airtimeMargin.toFixed(2)}%` : `${airtimeMargin.toFixed(2)}%`}
                    </Typography>
                    {vipAirtimeDiscount > 0 && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 0.5 }}>
                        VIP Tier Net Margin ({vipAirtimeDiscount}% discount):{' '}
                        <strong style={{ color: airtimeVipMargin >= 0 ? '#16A34A' : '#DC2626' }}>
                          {airtimeVipMargin >= 0 ? `+${airtimeVipMargin.toFixed(2)}%` : `${airtimeVipMargin.toFixed(2)}%`}
                        </strong>
                      </Typography>
                    )}
                  </div>
                  <Chip
                    icon={airtimeMargin >= 0 ? <Sparkles size={14} /> : <AlertTriangle size={14} />}
                    label={airtimeMargin >= 0 ? 'Profitable Rate' : 'Loss-Making'}
                    color={airtimeMargin >= 0 ? 'success' : 'error'}
                    size="small"
                  />
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={isElectricity ? 'Service Surcharge / Fee (₦)' : 'Retail Selling Price (₦)'}
                    placeholder="e.g. 12500"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vendor Wholesale Cost (₦)"
                    placeholder="e.g. 11500"
                    value={formData.buyingAmount}
                    onChange={(e) => setFormData({ ...formData, buyingAmount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="VIP Price (₦)"
                    placeholder="e.g. 12000"
                    value={formData.vipAmount}
                    onChange={(e) => setFormData({ ...formData, vipAmount: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cashback (₦)"
                    placeholder="0"
                    value={formData.cashback}
                    onChange={(e) => setFormData({ ...formData, cashback: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Referral Bonus (₦)"
                    placeholder="0"
                    value={formData.referralBonus}
                    onChange={(e) => setFormData({ ...formData, referralBonus: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Live Cash Margin Box - Apple Inset Card with Real Unit Economics */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '18px',
                  bgcolor: netMargin < 0 ? 'rgba(220, 38, 38, 0.035)' : 'rgba(22, 163, 74, 0.035)',
                  border: '1px solid',
                  borderColor: netMargin < 0 ? 'rgba(220, 38, 38, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Top Row: Primary Metric & Status Badge */}
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
                  <div>
                    <Typography sx={{ color: '#4B5563', fontSize: '0.85rem', fontWeight: 650, letterSpacing: '-0.01em' }}>
                      Estimated Net Margin per Sale
                    </Typography>
                    <Stack direction="row" alignItems="baseline" spacing={1} mt={0.25}>
                      <Typography
                        sx={{
                          fontSize: '1.75rem',
                          fontWeight: 800,
                          color: netMargin >= 0 ? '#16A34A' : '#DC2626',
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '-0.02em',
                          lineHeight: 1.2,
                        }}
                      >
                        {netMargin >= 0 ? `+₦${netMargin.toLocaleString()}` : `-₦${Math.abs(netMargin).toLocaleString()}`}
                      </Typography>
                      {retailAmount > 0 && (
                        <Typography
                          sx={{
                            fontSize: '0.88rem',
                            fontWeight: 700,
                            color: netMargin >= 0 ? '#16A34A' : '#DC2626',
                          }}
                        >
                          ({netMargin >= 0 ? `+${netMarginPercent.toFixed(1)}%` : `${netMarginPercent.toFixed(1)}%`} margin)
                        </Typography>
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.25 }}>
                      Net margin after deducting vendor wholesale cost, buyer cashback, and referral rewards.
                    </Typography>
                  </div>

                  <Chip
                    icon={netMargin >= 0 ? <Sparkles size={14} /> : <AlertTriangle size={14} />}
                    label={netMargin >= 0 ? 'Profitable' : 'Loss-Making'}
                    color={netMargin >= 0 ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 700, px: 0.5 }}
                  />
                </Stack>

                {/* Apple Inset Breakdown Grid */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Gross Spread
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: grossMargin >= 0 ? '#16A34A' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                        {grossMargin >= 0 ? `+₦${grossMargin.toLocaleString()}` : `-₦${Math.abs(grossMargin).toLocaleString()}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Retail − Wholesale
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Buyer Cashback
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: cashbackAmount > 0 ? '#DC2626' : '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
                        {cashbackAmount > 0 ? `-₦${cashbackAmount.toLocaleString()}` : '₦0'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Wallet credit
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Referral Reward
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: referralAmount > 0 ? '#DC2626' : '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
                        {referralAmount > 0 ? `-₦${referralAmount.toLocaleString()}` : '₦0'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Referrer credit
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Total Cost Outflow
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                        ₦{totalExpenses.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Wholesale + Rewards
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* VIP Price Margin Pill if set */}
                {hasVipPrice && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.25,
                      borderRadius: '10px',
                      bgcolor: '#FFFFFF',
                      border: '1px solid',
                      borderColor: vipNetMargin >= 0 ? 'rgba(22, 163, 74, 0.25)' : 'rgba(220, 38, 38, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>
                        VIP Tier Net Profit (₦{vipPrice.toLocaleString()} selling price):
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.88rem',
                          fontWeight: 800,
                          color: vipNetMargin >= 0 ? '#16A34A' : '#DC2626',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {vipNetMargin >= 0 ? `+₦${vipNetMargin.toLocaleString()}` : `-₦${Math.abs(vipNetMargin).toLocaleString()}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: vipNetMargin >= 0 ? '#16A34A' : '#DC2626' }}>
                        ({vipNetMargin >= 0 ? `+${vipNetMarginPercent.toFixed(1)}%` : `${vipNetMarginPercent.toFixed(1)}%`})
                      </Typography>
                    </Stack>

                    <Chip
                      label={vipNetMargin >= 0 ? 'VIP Profitable' : 'VIP Loss'}
                      color={vipNetMargin >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  </Box>
                )}

                {/* Loss Warning Banner */}
                {netMargin < 0 && (
                  <Alert
                    severity="error"
                    icon={<AlertTriangle size={16} />}
                    sx={{
                      mt: 1.5,
                      py: 0.5,
                      borderRadius: '10px',
                      fontSize: '0.78rem',
                      bgcolor: 'rgba(220, 38, 38, 0.08)',
                      color: '#991B1B',
                      border: '1px solid rgba(220, 38, 38, 0.2)',
                    }}
                  >
                    <strong>Negative Margin Alert:</strong> Total expenses (Wholesale ₦{wholesaleCost.toLocaleString()} + Cashback ₦{cashbackAmount.toLocaleString()} + Referral ₦{referralAmount.toLocaleString()} = <strong>₦{totalExpenses.toLocaleString()}</strong>) exceed the retail price (<strong>₦{retailAmount.toLocaleString()}</strong>). You will lose <strong>₦{Math.abs(netMargin).toLocaleString()}</strong> on every retail order.
                  </Alert>
                )}
              </Box>
            </Stack>
          )}
        </Card>

        {/* 4. Multi-Processor Routing (V2 Engine) Card */}
        <MultiProcessorRoutingCard
          useV2Engine={useV2Engine}
          onChangeUseV2Engine={setUseV2Engine}
          processorRoutes={processorRoutes}
          onChangeRoutes={setProcessorRoutes}
          selectedService={selectedService}
          providers={providers}
          onSyncPlan={handleSyncPlan}
          onPrimaryPlanChange={(newPlanId) => {
            setFormData((prev) => ({ ...prev, planId: newPlanId }));
          }}
          onPrimaryNetworkChange={(newNetId) => {
            setFormData((prev) => ({ ...prev, planNetworkId: String(newNetId) }));
          }}
          fallbackPlanId={formData.planId}
          fallbackNetworkId={formData.planNetworkId}
          fallbackBuyingAmount={formData.buyingAmount}
        />

        {/* 5. Product Catalog Settings & Distribution Channels */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 4,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            5. Catalog Visibility & Distribution Channels
          </Typography>

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.available}
                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                    color="success"
                  />
                }
                label={
                  <div>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Available for Purchase
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.775rem' }}>
                      Allow users to purchase this product immediately
                    </Typography>
                  </div>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hidden}
                    onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
                    color="warning"
                  />
                }
                label={
                  <div>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Hide from App Catalog
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.775rem' }}>
                      Keep active on API but hide from mobile app
                    </Typography>
                  </div>
                }
              />
            </Stack>

            <Divider />

            <div>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', mb: 1.5 }}>
                Enabled Sales Channels
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowApi}
                      onChange={(e) => setFormData({ ...formData, allowApi: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <div>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        API Reselling
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                        Available via REST API for third-party developers
                      </Typography>
                    </div>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowUssd}
                      onChange={(e) => setFormData({ ...formData, allowUssd: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <div>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        USSD Channel
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                        Listed on offline *384# USSD menus
                      </Typography>
                    </div>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowWhatsapp}
                      onChange={(e) => setFormData({ ...formData, allowWhatsapp: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <div>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        WhatsApp Bot
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                        Purchasable via automated WhatsApp chat
                      </Typography>
                    </div>
                  }
                />
              </Stack>
            </div>
          </Stack>
        </Card>

        {/* Bottom Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mb={6}>
          <Button
            onClick={() => navigate(`/dashboard/services/details/${product.id}`)}
            sx={{
              borderRadius: '24px',
              bgcolor: '#F1F3F5',
              color: '#374151',
              px: 3.5,
              py: 1.1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveProduct}
            disabled={saving}
            sx={{
              borderRadius: '24px',
              bgcolor: '#0066FE',
              color: '#FFFFFF',
              px: 4,
              py: 1.1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0052CC', boxShadow: 'none' },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save changes'}
          </Button>
        </Box>
      </Container>
    </Page>
  );
}
